import React, { useContext, useEffect, useState } from "react";
import {
    Layout,
    Card,
    Typography,
    Select,
    Button,
    Table,
    Space,
    Modal,
    Form,
    Input,
    Popconfirm,
    Tag,
    message,
    Tooltip
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
    DatabaseOutlined,
    PlusOutlined,
    DeleteOutlined,
    CodeOutlined,
    EyeInvisibleOutlined,
    InfoCircleOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import AppSider from "../components/AppSider";
import { AuthContext } from "../../../config/AuthContext";
import { GetAules } from "../../../dao/GetAules";
import { GetBbddActivitats } from "../../../dao/bbdd/GetBbddActivitats";
import {
    AulaDatabase,
    GetAulaDatabases,
    PostAulaDatabase,
    DeleteAulaDatabase
} from "../../../dao/bbdd/AulaDatabasesApi";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const AulesDatabases: React.FC = () => {
    const { userId, user } = useContext(AuthContext);
    const [aules, setAules] = useState<any[]>([]);
    const [selectedAula, setSelectedAula] = useState<number | null>(null);
    const [loadingAules, setLoadingAules] = useState<boolean>(false);

    const [databases, setDatabases] = useState<AulaDatabase[]>([]);
    const [loadingDatabases, setLoadingDatabases] = useState<boolean>(false);

    const [modalVisible, setModalVisible] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [form] = Form.useForm();

    useEffect(() => {
        if (userId) {
            loadAules();
        }
    }, [userId]);

    useEffect(() => {
        if (selectedAula) {
            loadDatabases(selectedAula);
        } else {
            setDatabases([]);
        }
    }, [selectedAula]);

    const loadAules = async () => {
        setLoadingAules(true);
        try {
            const [data, activitats] = await Promise.all([
                GetAules(String(userId)),
                GetBbddActivitats(String(userId))
            ]);

            // Obtenir IDs únics d'aules que tenen activitats de base de dades
            const aulesAmbBbdd = new Set<number>();
            if (Array.isArray(activitats)) {
                activitats.forEach((act: any) => {
                    const aId = act.id_aula || act.idAula;
                    if (aId) aulesAmbBbdd.add(Number(aId));
                });
            }

            if (Array.isArray(data)) {
                // Filtrar només aquelles aules que tenen com a mínim una activitat de BBDD
                const filteredAules = data.filter((a: any) => {
                    const aId = Number(a.id || a.key);
                    return aulesAmbBbdd.has(aId);
                });

                setAules(filteredAules);
                if (filteredAules.length > 0) {
                    const firstId = filteredAules[0].id || filteredAules[0].key;
                    setSelectedAula(Number(firstId));
                } else {
                    setSelectedAula(null);
                }
            }
        } catch (error) {
            console.error("Error loading aules:", error);
        } finally {
            setLoadingAules(false);
        }
    };

    const loadDatabases = async (aulaId: number) => {
        setLoadingDatabases(true);
        try {
            const list = await GetAulaDatabases(aulaId);
            setDatabases(list || []);
        } catch (error) {
            console.error("Error loading aula databases:", error);
        } finally {
            setLoadingDatabases(false);
        }
    };

    const handleCreate = async () => {
        try {
            const values = await form.validateFields();
            if (!selectedAula) {
                message.warning("Selecciona una aula abans de crear la base de dades");
                return;
            }
            setSubmitting(true);
            await PostAulaDatabase(selectedAula, values.name);
            message.success("Base de dades d'aula creada amb èxit");
            form.resetFields();
            setModalVisible(false);
            loadDatabases(selectedAula);
        } catch (error: any) {
            console.error("Error creating aula db:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!selectedAula) return;
        try {
            await DeleteAulaDatabase(selectedAula, id);
            message.success("Base de dades eliminada correctament");
            loadDatabases(selectedAula);
        } catch (error) {
            console.error("Error deleting aula db:", error);
        }
    };

    const handleOpenInIde = (schemaName: string) => {
        const ideUrl = `/database/ide/frame?schema=${encodeURIComponent(schemaName)}`;
        window.open(ideUrl, "_blank");
    };

    const columns: ColumnsType<AulaDatabase> = [
        {
            title: "Nom Lògic",
            dataIndex: "nom",
            key: "nom",
            render: (text: string) => (
                <Space>
                    <DatabaseOutlined style={{ color: "#1890ff" }} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: "Esquema MySQL",
            dataIndex: "schema_name",
            key: "schema_name",
            render: (text: string) => (
                <Tag color="geekblue" style={{ fontFamily: "monospace" }}>
                    {text}
                </Tag>
            )
        },
        {
            title: "Visibilitat per Alumnes",
            key: "visibility",
            render: () => (
                <Tag icon={<EyeInvisibleOutlined />} color="default">
                    Oculta (Accés de només lectura en exercicis vinculats)
                </Tag>
            )
        },
        {
            title: "Professor Creador",
            dataIndex: "professor_nom",
            key: "professor_nom",
            render: (text: string) => text || "Docent"
        },
        {
            title: "Data Creació",
            dataIndex: "created_at",
            key: "created_at",
            render: (text: string) => (text ? new Date(text).toLocaleString("ca-ES") : "-")
        },
        {
            title: "Accions",
            key: "actions",
            align: "center",
            render: (_: any, record: AulaDatabase) => (
                <Space>
                    <Tooltip title="Obrir al IDE per inicialitzar taules, dades i triggers">
                        <Button
                            type="primary"
                            icon={<CodeOutlined />}
                            size="small"
                            onClick={() => handleOpenInIde(record.schema_name)}
                        >
                            Obrir al IDE
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="Segur que vols eliminar aquesta base de dades d'aula?"
                        description="S'esborrarà completament l'esquema físic i les taules de MySQL."
                        okText="Sí, eliminar"
                        cancelText="Cancel·lar"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record.id)}
                    >
                        <Button danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <>
            <AppSider currentVal={8} />
            <Content style={{ padding: "0 24px", minHeight: 280 }}>
                <Card
                    style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                        title={
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Space>
                                    <DatabaseOutlined style={{ fontSize: 24, color: "#1890ff" }} />
                                    <div>
                                        <Title level={4} style={{ margin: 0 }}>
                                            Bases de Dades d'Aula (Només Lectura per a Alumnes)
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            Crea i gestiona esquemes de consulta per als exercicis d'aquesta aula.
                                        </Text>
                                    </div>
                                </Space>
                                <Space>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={() => selectedAula && loadDatabases(selectedAula)}
                                    >
                                        Refrescar
                                    </Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setModalVisible(true)}
                                        disabled={!selectedAula}
                                    >
                                        Nova Base de Dades
                                    </Button>
                                </Space>
                            </div>
                        }
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                marginBottom: 20,
                                background: "#fafafa",
                                padding: "12px 16px",
                                borderRadius: 6
                            }}
                        >
                            <Text strong>Aula:</Text>
                            <Select
                                style={{ minWidth: 260 }}
                                placeholder="Selecciona una aula"
                                value={selectedAula}
                                loading={loadingAules}
                                onChange={(val) => setSelectedAula(val)}
                                options={aules.map((a) => ({
                                    label: a.nom || a.label || `Aula ${a.id || a.key}`,
                                    value: Number(a.id || a.key)
                                }))}
                            />
                            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                                <InfoCircleOutlined style={{ color: "#faad14" }} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Aquestes bases de dades estan <strong>ocultes</strong> per als alumnes i només són accessibles en exercicis vinculats en mode consulta.
                                </Text>
                            </div>
                        </div>

                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={databases}
                            loading={loadingDatabases}
                            pagination={false}
                            locale={{
                                emptyText: selectedAula
                                    ? "Encara no hi ha cap base de dades creada en aquesta aula."
                                    : "Selecciona una aula per veure les bases de dades"
                            }}
                        />
                    </Card>

                    <Modal
                        title={
                            <Space>
                                <DatabaseOutlined style={{ color: "#1890ff" }} />
                                <span>Crear Nova Base de Dades per a l'Aula</span>
                            </Space>
                        }
                        open={modalVisible}
                        onCancel={() => {
                            form.resetFields();
                            setModalVisible(false);
                        }}
                        onOk={handleCreate}
                        confirmLoading={submitting}
                        okText="Crear Base de Dades"
                        cancelText="Cancel·lar"
                    >
                        <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
                            Es crearà un esquema MySQL físic amb el prefix <code>aula_{selectedAula}_[nom]</code>. Podràs obrir-lo al teu IDE per crear taules i omplir les dades de prova necessàries.
                        </Paragraph>
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="name"
                                label="Nom de la Base de Dades"
                                rules={[
                                    { required: true, message: "El nom és obligatori" },
                                    {
                                        pattern: /^[a-zA-Z0-9_]+$/,
                                        message: "Només caràcters alfanumèrics i guió baix (sense espais)"
                                    }
                                ]}
                            >
                                <Input placeholder="p. ex. botiga, universitat, biblioteca..." autoFocus />
                            </Form.Item>
                        </Form>
                    </Modal>
                </Content>
        </>
    );
};

export default AulesDatabases;
